import React, { useEffect, useState } from 'react';
import './MakePlan.scss';
import { callFeltMediator } from '../../../utils/feltMediator';
import { i18n } from '../../../i18n';
import { useTranslation } from 'react-i18next';
import CopyDivToClipBoardButton from '../../CopyDivToClipBoardButton/CopyDivToClipBoardButton';

function MakePlan({ mirrorData, updateValue, name, partnerName, side1Gender, side1Animal, side2Gender, side2Animal, relation }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [plan, setPlan] = useState(null);
  const { t } = useTranslation();
  const aiSummary = mirrorData?.journeyProps?.AISummary || {};
  const suggestions = Array.isArray(aiSummary?.suggestions) ? aiSummary.suggestions : [];

 
  useEffect(() => {
    const selected = suggestions.find(s => s && s.selected);
    setSelectedIdea(selected || null);
    setError(null); // Reset error when selected idea changes
  }, [suggestions]);

  const generatePlan = async (force = false) => {
      console.log('MakePlan: generatePlan called', { force, hasSelectedIdea: !!selectedIdea });
      if (!selectedIdea) { setLoading(false); return; }
      const hasExisting = selectedIdea.plan && ((selectedIdea.plan.planTodoList && selectedIdea.plan.planTodoList.length) || selectedIdea.plan.planWhy);
      if (!force && hasExisting) {
        console.log('MakePlan: using existing plan from DB');
        setPlan(selectedIdea.plan);
        setLoading(false);
        setError(null);
        return;
      }

      // No existing plan on the newly selected idea → clear any previous plan while generating
      if (!force && !hasExisting) {
        console.log('MakePlan: no existing plan, generating new');
        setPlan(null);
      }
      setLoading(true);
      setError(null);
      const recapName = aiSummary?.mirrorName || '';
      const recapSummary = aiSummary?.mirrorDescription || '';
      const ideaTitle = selectedIdea?.title || '';
      const ideaDescription = selectedIdea?.description || '';
      const ideaWhy = selectedIdea?.why || '';

      const LANGUAGE_LABELS = {
        en: 'English',
        he: 'Hebrew',
        ar: 'Arabic'
      };
      const language = i18n.language;
      const targetLanguageLabel = LANGUAGE_LABELS[language] || language;
      console.log('MakePlan: language context', { language, targetLanguageLabel });
      let prompt = '';
      prompt += `You are a friendly mediator app.\n `
      prompt += 'You are an expert in using professional principles from conflict resolution, therapy, mediation, Social emotional learning and education — including methods and frameworks such as Cognitive Behavioral Therapy (CBT), Nonviolent Communication (NVC), Social-Emotional Learning (SEL), Positive Psychology, Narrative Therapy, Conflict Resolution, Restorative Practices, Play Therapy, and Attachment Theory. Use concepts like emotional awareness, cognitive restructuring, behavior activation, communication skills, self-regulation, empathy building, co-regulation, perspective-taking, and validation.\n';
      prompt += 'This is a session between a user who is a young child and you.\n';
      prompt += 'The child (name) just mirrored a relationship he has with someone else (partnerName).\n';
      prompt += 'You are talking directly to the child.\n\n';
      prompt += `Context:\n`;
      prompt += `- Situation name: ${recapName}\n`;
      prompt += `- Situation summary: ${recapSummary}\n`;
      prompt += `- Selected idea title: ${ideaTitle}\n`;
      prompt += `- Selected idea description: ${ideaDescription}\n`;
      prompt += `- Selected idea why: ${ideaWhy}\n\n`;
      prompt += `- Child name: ${name}\n`;
      prompt += `- Child gender: ${side1Gender}\n`;
      prompt += `- Child animal: ${side1Animal}\n`;
      prompt += `- Partner name: ${partnerName}\n`;
      prompt += `- Partner gender: ${side2Gender}\n`;
      prompt += `- Partner animal: ${side2Animal}\n`;
      prompt += `- Relation: ${relation}\n\n`;
      prompt += `Task:\n`;
      prompt += `A) Produce a concise plan:\n`;
      if (language !== 'en') {
        prompt += `Please respond ONLY in ${targetLanguageLabel}, including all following text:\n`;
      }
      prompt += `   1. planName: a short name for the plan. \n`;
      prompt += `   2. planDescription: write a short description of the plan and how it works in high level. Address the child directly. Use very simple language. You may use emojis to help convey the plan. \n`;
      prompt += `   3. planTodoList: a step-by-step list that tells the child exactly what to do - like a recipe. Each step needs to be actounable, if needed use examples to help the child understand exectly what he/she needs to do. Address the child directly. Use very simple language. You may use emojis to clarify the action. \n`;
      prompt += `   when suggesting the child what to say, give an example, as its difficult to transform ideas into words. \n`;
      prompt += `   4. planGoal: one short sentence describing the desired outcome (what we want to achieve by doing the plan).\n`;
      prompt += `   5. planWhyList: a short list explaining what do both sides get out of the plan, does it really solve a problem? What is it good for?\n`;
      prompt += `   6. planWhyExplanation: a short explanation about what do both sides get out of the plan, does it really solve a problem? What is it good for?\n`;
      prompt += `IMPORTANT: Do not add numbers or bullets to the steps of planTodoList and/or planWhy yourself (this will be added in the html).\n`;
      prompt += `IMPORTANT: All texts are for a little child. make sure the child can understnad, it's in simple language, not too long but not too short, caring and gentle, especially when talking about sensitive topics.\n`;
      if (language !== 'en') {
        prompt += `Please respond ONLY in ${targetLanguageLabel}, including all texts.`;
      }

      console.log('MakePlan: prompt length', (prompt || '').length);
      const functionDefinition = {
        name: 'generate_plan_for_selected_idea',
        description: 'Generate a simple plan (todos + why) for the selected idea',
        parameters: {
          type: 'object',
          properties: {
            planName: { type: 'string' },
            planDescription: { type: 'string' },
            planTodoList: { type: 'array', items: { type: 'string' }, minItems: 2, maxItems: 6 },
            planGoal: { type: 'string' },
            planWhy: { type: 'array', items: { type: 'string' }, minItems: 1, maxItems: 6 },
            planWhyExplanation: { type: 'string' }
          },
          required: ['planDescription', 'planTodoList', 'planGoal', 'planWhy', 'planWhyExplanation']
        }
      };

      try {
        const resp = await callFeltMediator({ prompt, numberOfSuggestions: 0, shouldGenerateMirrorNameAndDescription: false, functionDefinition });
        const newPlan = {
          planName: resp?.planName || '',
          planDescription: resp?.planDescription || '',
          planTodoList: Array.isArray(resp?.planTodoList) ? resp.planTodoList : [],
          planGoal: resp?.planGoal || '',
          planWhy: Array.isArray(resp?.planWhy) ? resp.planWhy : (resp?.planWhy ? [resp.planWhy] : []),
          planWhyExplanation: resp?.planWhyExplanation || ''
        };
        console.log('MakePlan: planTodoList', newPlan.planTodoList);
        console.log('MakePlan: planGoal', newPlan.planGoal);
        console.log('MakePlan: planWhy', newPlan.planWhy);
        console.log('MakePlan: planWhyExplanation', newPlan.planWhyExplanation);
        const nextSuggestions = suggestions.map(s => (s === selectedIdea ? { ...s, plan: newPlan } : s));
        setPlan(newPlan);
        if (updateValue) {
          updateValue({
            ...aiSummary,
            suggestions: nextSuggestions
          });
        }
      } catch (e) {
        console.error('MakePlan AI failed', e);
        setError(true);
        setPlan(null); // Ensure plan is cleared on error
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    const run = async () => {
      await generatePlan();
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdea]);

  const handleRegenerate = async () => {
    console.log('MakePlan: re-create button clicked');
    setIsRegenerating(true);
    // keep existing plan rendered but dimmed
    await generatePlan(true);
    setIsRegenerating(false);
  };

  if (!selectedIdea) {
    return (
      <div className="make-plan">
        <div className="answerAI">
          <div className="typing"><div className="dot"></div><div className="dot"></div><div className="dot"></div></div>
        </div>
        <p>{t('MakePlan.noIdeaSelected')}</p>
      </div>
    );
  }

  const hasExistingPlanOnSelected = !!(selectedIdea?.plan && (
    (Array.isArray(selectedIdea.plan.planTodoList) && selectedIdea.plan.planTodoList.length > 0) ||
    (Array.isArray(selectedIdea.plan.planWhy) && selectedIdea.plan.planWhy.length > 0) ||
    selectedIdea.plan.planDescription ||
    selectedIdea.plan.planGoal ||
    selectedIdea.plan.planName ||
    selectedIdea.plan.planWhyExplanation
  ));
  const showInitialSpinner = loading && !isRegenerating && !hasExistingPlanOnSelected;
  console.log('MakePlan UI state', { loading, isRegenerating, hasExistingPlanOnSelected, showInitialSpinner });

  return (
    <div className="make-plan">
        {/* idea summery */}
        {/* <label className="form-label noselect form-label-title">{t('MakePlan.idea-title')}</label>
        <div className="mini-idea-card">
          {selectedIdea.emoji && <div className="mini-idea-card-emoji">{selectedIdea.emoji}</div>}
          {selectedIdea.title ? <div className="mini-idea-card-title"> {selectedIdea?.title}</div> : (selectedIdea?.description && <div className="mini-idea-card-title"> {selectedIdea?.description}</div>)}
        </div> */}

        {/* PLAN ////////////////////////////////////////////////////// */}
        <label className="form-label noselect form-label-title ">{t('MakePlan.make-plan-title')}</label>
        
        {error && (
          <div className="make-plan-error">
            <p>{t('recap.timeoutError') || 'Something went wrong. Please try again.'}</p>
            <button className="miniPanel__button" onClick={handleRegenerate}>
              {t('MakePlan.recreate') || 'Retry'}
            </button>
          </div>
        )}

        {showInitialSpinner && !error && (
          <div className="answerAI">
            <div className="typing"><div className="dot"></div><div className="dot"></div><div className="dot"></div></div>
          </div>
        )}

        {!showInitialSpinner && !error && (
        <div className="plan-content" style={{ position: 'relative', opacity: isRegenerating ? 0.4 : 1 }}>
          <div style={{ position: 'absolute', insetInlineEnd: 10, insetBlockStart: 10 }}>
            <CopyDivToClipBoardButton
              onClick={() => {
                try {
                  const range = document.createRange();
                  range.selectNode(document.querySelector('.plan-content'));
                  window.getSelection().removeAllRanges();
                  window.getSelection().addRange(range);
                  document.execCommand('copy');
                  window.getSelection().removeAllRanges();
                } catch (_) {}
              }}
              buttonLayout="textNextToBtn"
              buttonStyle="secondary"
              buttonSize="small"
              isIconWhite={false}
              isShowText={false}
            />
            
            
          </div>
          <div className="plan-header">
          {plan?.planName && <p className="plan-content-name">{plan.planName}</p>}
          {plan?.planDescription && <p className="plan-content-description">{plan.planDescription}</p>}
          {plan?.planGoal && <p className="plan-content-goal"><strong>{t('MakePlan.goal')}:</strong> {plan.planGoal}</p>}
          </div>

          <div className="plan-todo">
          <div className="plan-content-todo-list-title">{t('MakePlan.todoList-title')}</div>
          {Array.isArray(plan?.planTodoList) && plan.planTodoList.length > 0 && (
            <ol className="plan-content-todo-list">
              {plan.planTodoList.map((item, idx) => (
                <li className="plan-content-todo" key={idx}>{item}</li>
              ))}
            </ol>
          )}
          </div>

          <div className="plan-why">
          {/* user wants to know why  */}
          <div className="plan-content-why-title">{t('MakePlan.whyTitle')}</div>
          {Array.isArray(plan?.planWhy) && plan.planWhy.length > 0 && (
            <ul>
              {plan.planWhy.map((w, idx) => (
                <li className="plan-content-why" key={idx}>{w}</li>
              ))}
            </ul>
          )}
          {plan?.planWhyExplanation && <p className="plan-content-explanation"><strong>{t('MakePlan.explanation')}:</strong> {plan.planWhyExplanation}</p>}
          </div>
        </div>
        )}

        
        {(!showInitialSpinner || isRegenerating) && !error && (
          <div className="miniPanel">
            <div className="miniPanel__buttons-container">
              {!isRegenerating ? (
                <div className="miniPanel__button miniPanel__button-secondary" onClick={handleRegenerate}>
                  + {t('MakePlan.recreate')}
                </div>
              ) : (
                <div className="answerAI">
                  <div className="typing"><div className="dot"></div><div className="dot"></div><div className="dot"></div></div>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default MakePlan;